import { useEffect, useRef, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, API, explainSaveError } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { Building2, Upload, Trash2, Loader2, Save, Palette, ImageOff } from "lucide-react";
import { TIME_ZONE_OPTIONS, detectBrowserTimeZone } from "@/lib/timeZones";

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const LOGO_ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";
const LOGO_MAX_MB = 2;

const emptyProfile = {
  company_name: "",
  dba: "",
  phone: "",
  email: "",
  website: "",
  address: { line1: "", line2: "", city: "", state: "", zip: "" },
  license_number: "",
  license_state: "",
  license_expires: "",
  ein: "",
  service_area_notes: "",
  trade_categories: [],
  primary_trade: "",
  brand_primary_hex: "",
  brand_accent_hex: "",
  time_zone: "",
  has_logo: false,
  logo_url: null,
  // Slice 2O · one simple company-level toggle.
  auto_customer_updates_enabled: false,
};

const CompanyProfile = () => {
  const { t } = useLang();
  const [form, setForm] = useState(emptyProfile);
  const [tradeChoices, setTradeChoices] = useState([]);
  const [customTrade, setCustomTrade] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoBust, setLogoBust] = useState(Date.now());
  const [logoBlobUrl, setLogoBlobUrl] = useState(null);
  const logoInputRef = useRef(null);
  // Slice 2O · the `auto_customer_updates_enabled` toggle lives in the
  // existing `db.pricing_profiles` document (single source of truth
  // consumed by the backend notification hook). Company Profile page
  // keeps its own PUT /company-profile whitelist unchanged; we round-
  // trip this one toggle through GET/PUT /pricing-profile, preserving
  // every other pricing-profile field via a snapshot ref.
  const pricingSnapshotRef = useRef(null);

  const fetchLogoBlob = async () => {
    try {
      const token = localStorage.getItem("jc_token");
      const res = await fetch(`${API}/company-profile/logo/${form.user_id || "me"}?t=${logoBust}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setLogoBlobUrl(null);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setLogoBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch {
      setLogoBlobUrl(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/company-profile"),
      api.get("/company-profile/trade-choices"),
      api.get("/pricing-profile"),
    ])
      .then(([p, tc, pp]) => {
        if (cancelled) return;
        // Snapshot the current pricing profile so `save()` can PUT it back
        // with only the auto-notifications toggle updated. `_id` is
        // stripped in the API layer already, but be defensive.
        const pricingData = { ...(pp?.data || {}) };
        delete pricingData._id;
        pricingSnapshotRef.current = pricingData;
        setForm({
          ...emptyProfile,
          ...p.data,
          address: { ...emptyProfile.address, ...(p.data.address || {}) },
          auto_customer_updates_enabled:
            !!pricingData.auto_customer_updates_enabled,
        });
        setTradeChoices(Array.isArray(tc.data) ? tc.data : (tc.data?.choices || []));
      })
      .catch(() => toast.error(t("company_form.toast_load_failed")))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (form.has_logo) fetchLogoBlob();
    else {
      if (logoBlobUrl) URL.revokeObjectURL(logoBlobUrl);
      setLogoBlobUrl(null);
    }
  }, [form.has_logo, logoBust, form.user_id]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));
  const setAddress = (name, value) =>
    setForm((prev) => ({ ...prev, address: { ...prev.address, [name]: value } }));

  const toggleTrade = (label) => {
    setForm((prev) => {
      const has = prev.trade_categories.includes(label);
      return {
        ...prev,
        trade_categories: has
          ? prev.trade_categories.filter((t) => t !== label)
          : [...prev.trade_categories, label],
      };
    });
  };

  const addCustomTrade = () => {
    const v = customTrade.trim();
    if (!v) return;
    if (v.length > 60) { toast.error("Trade label too long"); return; }
    if (form.trade_categories.includes(v)) { toast.info("Already added"); return; }
    setForm((prev) => ({ ...prev, trade_categories: [...prev.trade_categories, v] }));
    setCustomTrade("");
  };

  const validate = () => {
    if (form.brand_primary_hex && !HEX_RE.test(form.brand_primary_hex)) {
      toast.error("Primary color must be a hex like #0f2540");
      return false;
    }
    if (form.brand_accent_hex && !HEX_RE.test(form.brand_accent_hex)) {
      toast.error("Accent color must be a hex like #ffb020");
      return false;
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      // iter48 · If the contractor never picked a time zone, fall back to a
      // best-effort browser IANA guess so downstream scheduling / PDF
      // timestamps always have a valid value.
      const tzToSave = (form.time_zone && form.time_zone.trim()) || detectBrowserTimeZone();
      const { data } = await api.put("/company-profile", {
        company_name: form.company_name,
        dba: form.dba,
        phone: form.phone,
        email: form.email,
        website: form.website,
        address: form.address,
        license_number: form.license_number,
        license_state: form.license_state,
        license_expires: form.license_expires,
        service_area_notes: form.service_area_notes,
        ein: form.ein || "",
        trade_categories: form.trade_categories,
        primary_trade: form.primary_trade,
        brand_primary_hex: form.brand_primary_hex,
        brand_accent_hex: form.brand_accent_hex,
        time_zone: tzToSave,
      });
      // Slice 2O · round-trip the auto-customer-updates toggle through
      // the pricing-profile document (single source of truth for the
      // backend notification hook). Uses the snapshot captured at mount
      // so every other pricing-profile field is preserved. The post-
      // save form state prefers server truth (the field returned by
      // PUT /pricing-profile at the top level) and falls back to the
      // user's chosen value so a malformed response can never silently
      // flip the toggle away from what the contractor selected.
      const snap = pricingSnapshotRef.current;
      let nextEnabled = !!form.auto_customer_updates_enabled;
      if (snap) {
        const pricingBody = {
          ...snap,
          auto_customer_updates_enabled: !!form.auto_customer_updates_enabled,
        };
        const { data: freshPricing } = await api.put(
          "/pricing-profile",
          pricingBody,
        );
        const nextSnap = { ...(freshPricing || {}) };
        delete nextSnap._id;
        pricingSnapshotRef.current = nextSnap;
        if (typeof freshPricing?.auto_customer_updates_enabled === "boolean") {
          nextEnabled = freshPricing.auto_customer_updates_enabled;
        }
      }
      setForm((prev) => ({
        ...prev,
        ...data,
        auto_customer_updates_enabled: nextEnabled,
      }));
      toast.success(t("company_form.toast_saved"));
    } catch (err) {
      toast.error(explainSaveError(err, "company profile"));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > LOGO_MAX_MB * 1024 * 1024) {
      toast.error(`Logo exceeds ${LOGO_MAX_MB} MB limit`);
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/company-profile/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, ...data }));
      setLogoBust(Date.now());
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the current logo?")) return;
    try {
      const { data } = await api.delete("/company-profile/logo");
      setForm((prev) => ({ ...prev, ...data }));
      setLogoBust(Date.now());
      toast.success("Logo removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  const monogram =
    (form.company_name || "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("") || "?";

  if (loading) {
    return (
      <DashboardLayout title={t("pages.company_profile.title")} subtitle={t("pages.company_profile.loading_subtitle")}>
        <div className="p-6 text-sm text-[var(--jc-text-soft)]">{t("company_form.loading_body")}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={t("pages.company_profile.title")}
      subtitle={t("pages.company_profile.subtitle")}
    >
      <div className="grid lg:grid-cols-3 gap-6" data-testid="company-profile-page">
        <div className="lg:col-span-2 space-y-6">
          <section className="jc-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Building2 size={16} className="text-[var(--jc-text-soft)]" />
              <div className="overline">{t("company_form.section_identity")}</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("company_form.company_name")} required>
                <input
                  data-testid="cp-company-name"
                  className="jc-input"
                  value={form.company_name}
                  onChange={(e) => setField("company_name", e.target.value)}
                  placeholder={t("company_form.placeholder_company")}
                />
              </Field>
              <Field label={t("company_extra.dba")}>
                <input
                  data-testid="cp-dba"
                  className="jc-input"
                  value={form.dba}
                  onChange={(e) => setField("dba", e.target.value)}
                />
              </Field>
              <Field label={t("company_form.phone")}>
                <input
                  data-testid="cp-phone"
                  className="jc-input"
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  placeholder={t("company_form.placeholder_phone")}
                />
              </Field>
              <Field label={t("company_form.email")}>
                <input
                  data-testid="cp-email"
                  type="email"
                  className="jc-input"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  placeholder={t("company_form.placeholder_email")}
                />
              </Field>
              <Field label={t("company_form.website")}>
                <input
                  data-testid="cp-website"
                  className="jc-input"
                  value={form.website}
                  onChange={(e) => setField("website", e.target.value)}
                  placeholder={t("company_form.placeholder_website")}
                />
              </Field>
              <Field label={t("company_form.timezone")}>
                <select
                  data-testid="cp-timezone"
                  className="jc-input"
                  value={form.time_zone || ""}
                  onChange={(e) => setField("time_zone", e.target.value)}
                >
                  <option value="">{t("company_form.tz_auto")}</option>
                  {TIME_ZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </Field>
            </div>
          </section>

          <section className="jc-card p-6">
            <div className="overline mb-4">{t("company_form.section_address")}</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("company_extra.line1")} wide>
                <input
                  data-testid="cp-address-line1"
                  className="jc-input"
                  value={form.address.line1}
                  onChange={(e) => setAddress("line1", e.target.value)}
                />
              </Field>
              <Field label={t("company_extra.line2")} wide>
                <input
                  data-testid="cp-address-line2"
                  className="jc-input"
                  value={form.address.line2}
                  onChange={(e) => setAddress("line2", e.target.value)}
                />
              </Field>
              <Field label={t("company_form.city")}>
                <input
                  data-testid="cp-address-city"
                  className="jc-input"
                  value={form.address.city}
                  onChange={(e) => setAddress("city", e.target.value)}
                />
              </Field>
              <Field label={t("company_form.state")}>
                <input
                  data-testid="cp-address-state"
                  className="jc-input"
                  value={form.address.state}
                  onChange={(e) => setAddress("state", e.target.value)}
                />
              </Field>
              <Field label={t("company_form.zip")}>
                <input
                  data-testid="cp-address-zip"
                  className="jc-input"
                  value={form.address.zip}
                  onChange={(e) => setAddress("zip", e.target.value)}
                />
              </Field>
            </div>
          </section>

          <section className="jc-card p-6">
            <div className="overline mb-4">{t("company_form.section_license")}</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("company_form.license_number")}>
                <input
                  data-testid="cp-license-number"
                  className="jc-input"
                  value={form.license_number}
                  onChange={(e) => setField("license_number", e.target.value)}
                />
              </Field>
              <Field label={t("company_extra.license_state")}>
                <input
                  data-testid="cp-license-state"
                  className="jc-input"
                  value={form.license_state}
                  onChange={(e) => setField("license_state", e.target.value)}
                />
              </Field>
              <Field label={t("company_form.license_expiration") + " (YYYY-MM-DD)"}>
                <input
                  data-testid="cp-license-expires"
                  className="jc-input"
                  value={form.license_expires}
                  onChange={(e) => setField("license_expires", e.target.value)}
                  placeholder="2027-12-31"
                />
              </Field>
              <Field label={t("company_form.license_type")}>
                <input
                  data-testid="cp-primary-trade"
                  className="jc-input"
                  value={form.primary_trade}
                  onChange={(e) => setField("primary_trade", e.target.value)}
                  placeholder={t("company_form.placeholder_license")}
                />
              </Field>
                <Field label="EIN (Federal Tax ID)">
                  <input
                    data-testid="cp-ein"
                    className="jc-input"
                  value={form.ein}
                  onChange={(e) => setField("ein", e.target.value)}
                  placeholder="XX-XXXXXXX"
                  maxLength={11}
                />
              </Field>
              <Field label={t("company_form.service_area")} wide>
                <textarea
                  data-testid="cp-service-area"
                  rows={2}
                  className="jc-input"
                  value={form.service_area_notes}
                  onChange={(e) => setField("service_area_notes", e.target.value)}
                  placeholder={t("company_form.placeholder_service_area")}
                />
              </Field>
            </div>
          </section>

          <section className="jc-card p-6">
            <div className="overline mb-4">{t("company_form.section_trades")}</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {tradeChoices.map((label) => {
                const active = form.trade_categories.includes(label);
                return (
                  <button
                    key={label}
                    type="button"
                    data-testid={`cp-trade-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    onClick={() => toggleTrade(label)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
                      active
                        ? "bg-[var(--jc-navy)] text-white border-[var(--jc-navy)]"
                        : "border-[var(--jc-border)] text-[var(--jc-text)] hover:bg-[var(--jc-surface-hover)]"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {form.trade_categories.some((t) => !tradeChoices.includes(t)) && (
              <div className="text-xs text-[var(--jc-text-soft)] mb-2">
                Custom categories:{" "}
                {form.trade_categories.filter((t) => !tradeChoices.includes(t)).map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 px-2 py-0.5 mr-1 mb-1 text-[11px] rounded-full bg-[var(--jc-blue)]/10 text-[var(--jc-blue)]"
                    data-testid={`cp-trade-custom-${t}`}
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => toggleTrade(t)}
                      className="ml-1"
                      aria-label={`Remove ${t}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                data-testid="cp-trade-custom-input"
                className="jc-input flex-1"
                value={customTrade}
                onChange={(e) => setCustomTrade(e.target.value)}
                placeholder="Add a custom trade (e.g., Gutters, Windows)"
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomTrade())}
              />
              <button
                type="button"
                onClick={addCustomTrade}
                data-testid="cp-trade-custom-add"
                className="px-3 py-2 text-xs font-semibold rounded-md border border-[var(--jc-border)] hover:bg-[var(--jc-surface-hover)]"
              >
                {t("company_form.add")}
              </button>
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <section className="jc-card p-6">
            <div className="flex items-center gap-2 mb-3">
              <Palette size={16} className="text-[var(--jc-text-soft)]" />
              <div className="overline">{t("company_form.section_branding")}</div>
            </div>
            <p className="text-xs text-[var(--jc-text-soft)] mb-4">
              {t("company_extra.branding_description")}
            </p>

            <div className="mb-4">
              <div className="text-xs font-semibold text-[var(--jc-text-soft)] mb-2">{t("company_form.logo")}</div>
              <div
                className="w-full h-32 rounded-md border border-dashed border-[var(--jc-border)] bg-[var(--jc-surface)] flex items-center justify-center overflow-hidden"
                data-testid="cp-logo-preview"
              >
                {form.has_logo && logoBlobUrl ? (
                  <img
                    src={logoBlobUrl}
                    alt="Contractor logo"
                    className="max-h-full max-w-full object-contain"
                    data-testid="cp-logo-img"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-1 text-[var(--jc-text-soft)]" data-testid="cp-logo-fallback">
                    <div className="w-12 h-12 rounded-md bg-[var(--jc-navy)] text-white flex items-center justify-center font-display text-lg">
                      {monogram}
                    </div>
                    <span className="text-[11px]">{t("company_extra.no_logo")}</span>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  data-testid="cp-logo-upload-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md jc-btn-primary disabled:opacity-60"
                >
                  {uploadingLogo ? <Loader2 className="animate-spin" size={12} /> : <Upload size={12} />}
                  {form.has_logo ? t("company_form.replace_logo") : t("company_form.upload_logo")}
                </button>
                {form.has_logo && (
                  <button
                    type="button"
                    onClick={removeLogo}
                    data-testid="cp-logo-remove-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-[var(--jc-border)] text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={12} /> {t("company_form.remove_logo")}
                  </button>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept={LOGO_ACCEPT}
                  className="hidden"
                  data-testid="cp-logo-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadLogo(f);
                    e.target.value = "";
                  }}
                />
              </div>
              <p className="text-[11px] text-[var(--jc-text-soft)] mt-2">{t("company_extra.logo_hint")} {LOGO_MAX_MB} {t("company_extra.mb_suffix")}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label={t("company_form.primary_color")}>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    data-testid="cp-brand-primary-picker"
                    value={form.brand_primary_hex || "#0f2540"}
                    onChange={(e) => setField("brand_primary_hex", e.target.value)}
                    className="w-10 h-10 rounded border border-[var(--jc-border)]"
                  />
                  <input
                    className="jc-input font-mono text-xs"
                    data-testid="cp-brand-primary-hex"
                    value={form.brand_primary_hex}
                    onChange={(e) => setField("brand_primary_hex", e.target.value)}
                    placeholder="#0f2540"
                  />
                </div>
              </Field>
              <Field label={t("company_form.accent_color")}>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    data-testid="cp-brand-accent-picker"
                    value={form.brand_accent_hex || "#ffb020"}
                    onChange={(e) => setField("brand_accent_hex", e.target.value)}
                    className="w-10 h-10 rounded border border-[var(--jc-border)]"
                  />
                  <input
                    className="jc-input font-mono text-xs"
                    data-testid="cp-brand-accent-hex"
                    value={form.brand_accent_hex}
                    onChange={(e) => setField("brand_accent_hex", e.target.value)}
                    placeholder="#ffb020"
                  />
                </div>
              </Field>
            </div>
          </section>

          <section className="jc-card p-6" data-testid="cp-customer-notifications-card">
            <div className="overline mb-2">{t("customer_notifications.section_overline")}</div>
            <h2 className="text-lg font-semibold mb-1">
              {t("customer_notifications.title")}
            </h2>
            <p className="text-xs text-[var(--jc-text-soft)] mb-3">
              {t("customer_notifications.helper")}
            </p>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="auto_customer_updates_enabled"
                data-testid="cp-auto-customer-updates-yes"
                checked={!!form.auto_customer_updates_enabled}
                onChange={() => setField("auto_customer_updates_enabled", true)}
              />
              {t("customer_notifications.choice_yes")}
            </label>
            <label className="inline-flex items-center gap-2 text-sm ml-4">
              <input
                type="radio"
                name="auto_customer_updates_enabled"
                data-testid="cp-auto-customer-updates-no"
                checked={!form.auto_customer_updates_enabled}
                onChange={() => setField("auto_customer_updates_enabled", false)}
              />
              {t("customer_notifications.choice_no")}
            </label>
          </section>

          <section className="jc-card p-6">
            <div className="overline mb-2">{t("company_extra.how_this_is_used")}</div>
            <ul className="text-xs text-[var(--jc-text-soft)] space-y-1.5">
              <li>{t("company_extra.how_bullet_1")}</li>
              <li>{t("company_extra.how_bullet_2")}</li>
              <li>{t("company_extra.how_bullet_3")}</li>
            </ul>
          </section>
        </aside>
      </div>

      <div className="sticky bottom-0 mt-6 py-4 px-6 bg-[var(--jc-bg)] border-t border-[var(--jc-border)] flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="cp-save-btn"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
        >
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
          {saving ? t("company_form.saving") : t("company_form.save")}
        </button>
      </div>
    </DashboardLayout>
  );
};

const Field = ({ label, wide, required, children }) => (
  <label className={`flex flex-col gap-1.5 ${wide ? "sm:col-span-2" : ""}`}>
    <span className="text-xs font-semibold text-[var(--jc-text-soft)]">
      {label} {required && <span className="text-red-600">*</span>}
    </span>
    {children}
  </label>
);

export default CompanyProfile;
